/**
 * Per-Tenant Fundraiser Short Domains
 *
 * Single source of truth for the vanity/short domains used in fundraiser
 * links (e.g. `gfolk.me/jane`). Short domains are host-locked per tenant at
 * the edge (gfolk.me → DGR, gentdrive.me → DGD), so a username must only be
 * linked on its own tenant's short domain — the wrong domain looks the
 * username up in the wrong shard and 404s.
 *
 * Consumed by the API (outgoing email trigger payloads, template previews)
 * and the admin app (`apps/admin/tenants/config.ts`).
 */

export const FUNDRAISER_SHORT_DOMAINS = {
  dgr: 'gfolk.me',
  dgd: 'gentdrive.me',
  uwr: 'unitedweride.global',
} as const satisfies Record<string, string>;

export type FundraiserShortDomainSlug = keyof typeof FUNDRAISER_SHORT_DOMAINS;

/**
 * Get the fundraiser short domain for a tenant slug, or `undefined` when the
 * tenant has no short domain configured.
 */
export function getFundraiserShortDomain(slug: string | null | undefined): string | undefined {
  if (!slug) return undefined;
  return (FUNDRAISER_SHORT_DOMAINS as Record<string, string>)[slug.toLowerCase()];
}

/**
 * Build the absolute short URL for a fundraiser username
 * (e.g. `https://gentdrive.me/jane`). Returns `undefined` when the tenant has
 * no short domain or no username is provided — callers should fall back to
 * the tenant-relative `/fundraiser/<username>` path.
 */
export function buildFundraiserShortUrl(
  slug: string | null | undefined,
  username: string | null | undefined,
): string | undefined {
  const domain = getFundraiserShortDomain(slug);
  if (!domain || !username) return undefined;
  return `https://${domain}/${username}`;
}
